import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Film, Loader2, Trash2, Upload } from 'lucide-react';
import { VideoUploadFolder } from '@/types';
import { videoApi } from '@/services/api';
import { Button } from '@/components/ui/Button';

const MAX_VIDEO_BYTES = 8 * 1024 * 1024;

interface UploadedVideoValue {
  key: string;
  url: string;
}

interface VideoUploadFieldProps {
  folder: VideoUploadFolder;
  videoKey: string;
  previewUrl?: string;
  onChange: (value: UploadedVideoValue) => void;
}

export const VideoUploadField = ({
  folder,
  videoKey,
  previewUrl = '',
  onChange,
}: VideoUploadFieldProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState(previewUrl);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    setLocalPreviewUrl(previewUrl);
  }, [previewUrl]);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError('');
    if (file.size > MAX_VIDEO_BYTES) {
      setUploadError('Video must be 8 MB or smaller.');
      event.target.value = '';
      return;
    }

    if (file.type !== 'video/mp4' && file.type !== 'video/webm') {
      setUploadError('Choose an MP4 or WebM video.');
      event.target.value = '';
      return;
    }

    setIsUploading(true);
    try {
      const uploaded = await videoApi.uploadVideo(file, folder);
      setLocalPreviewUrl(uploaded.url);
      onChange({ key: uploaded.key, url: uploaded.url });
    } catch (error) {
      setUploadError((error as Error).message || 'Video upload failed.');
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = () => {
    setUploadError('');
    setLocalPreviewUrl('');
    onChange({ key: '', url: '' });
  };

  return (
    <div className="w-full">
      <label className="form-label">Preview Video</label>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="grid gap-4 p-4 sm:grid-cols-[240px_minmax(0,1fr)] sm:items-start">
          <div className="flex aspect-video w-full items-center justify-center overflow-hidden rounded-lg border border-dashed border-slate-300 bg-slate-950">
            {localPreviewUrl ? (
              <video
                src={localPreviewUrl}
                className="h-full w-full object-contain"
                controls
                preload="metadata"
                playsInline
              />
            ) : (
              <div className="flex flex-col items-center gap-2 px-4 text-center text-sm text-slate-300">
                <Film className="h-7 w-7" />
                <span>Optional video preview</span>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                variant="secondary"
                disabled={isUploading}
                onClick={() => inputRef.current?.click()}
              >
                {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {isUploading ? 'Uploading...' : videoKey ? 'Replace Video' : 'Choose Video'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={isUploading || (!videoKey && !localPreviewUrl)}
                onClick={handleRemove}
              >
                <Trash2 className="h-4 w-4" />
                Remove
              </Button>
            </div>
            <p className="text-sm text-slate-600">
              MP4 or WebM, maximum 8 MB. This video opens from the play button on listing cards.
            </p>
            {uploadError ? <p className="text-sm font-medium text-red-600">{uploadError}</p> : null}
          </div>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/webm,.mp4,.webm"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
};
